/**
 * staff-avatar.ts — MỘT nguồn chân lý cho ảnh đại diện nhân sự trên FE.
 *
 * Ảnh nằm ở `staff.avatarUrl` (GET /staff) và có hai dạng:
 *   - con trỏ kho ảnh ERP: "profile-photos/<staffId>", có thể kèm dấu chống
 *     cache "?v=<hash>" do BE gắn khi nhân sự đổi ảnh;
 *   - URL tuyệt đối kéo về từ Casdoor.
 * Quy tắc initials/tint hợp nhất từ 7 biến thể cũ (spec 17/08/2026 §3.7).
 */
import { useContext, useMemo } from "react";
import { QueryClient, QueryClientContext, useQuery } from "@tanstack/react-query";
import { useStaff } from "@/features/staff/api";
import { api } from "@/lib/api/client";
import { imageUrl, type Variant } from "@/lib/upload";
import { freshOriginalUrl } from "@/lib/fresh-upload";
import type { components } from "@/lib/api/generated/schema";

type DirectoryEntry = components["schemas"]["StaffAvatarEntry"];
type AvatarSource = { id: string; name: string; avatarUrl?: string | null | undefined };

export type AvatarRecord = { id: string; name: string; avatarUrl: string | null };
export type AvatarMaps = {
  byId: Record<string, AvatarRecord>;
  byName: Record<string, AvatarRecord>;
};

/** Chữ cái đầu của (tối đa) 2 từ cuối tên — "Nguyễn Thu Hà" → "TH". */
export function staffInitials(name: string | null | undefined): string {
  // Chỉ nhận mẩu BẮT ĐẦU BẰNG CHỮ CÁI: hồ sơ hay có đuôi ghi chú trong ngoặc
  // ("… (nghỉ việc)"), lấy bừa mẩu cuối là in ra dấu ngoặc làm chữ viết tắt.
  const words = (name ?? "")
    .trim()
    .split(/\s+/)
    .filter((w) => /^\p{L}/u.test(w));
  if (words.length === 0) return "?";
  return words
    .slice(-2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

/** Pastel ổn định theo tên — cùng người luôn cùng màu. */
export function avatarTint(name: string | null | undefined): string {
  const hue = (((name ?? "").charCodeAt(0) || 0) * 37) % 360;
  return `hsl(${hue}, 35%, 80%)`;
}

export function normalizeName(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/** byName chỉ giữ tên DUY NHẤT — trùng tên thì bỏ để không gắn nhầm ảnh. */
export function buildAvatarMaps(staff: readonly AvatarSource[]): AvatarMaps {
  const byId: AvatarMaps["byId"] = {};
  const byName: AvatarMaps["byName"] = {};
  const nameCount = new Map<string, number>();
  for (const s of staff) {
    const rec: AvatarRecord = { id: s.id, name: s.name, avatarUrl: s.avatarUrl ?? null };
    byId[s.id] = rec;
    const key = normalizeName(s.name);
    nameCount.set(key, (nameCount.get(key) ?? 0) + 1);
    byName[key] = rec;
  }
  for (const [key, count] of nameCount) {
    if (count > 1) delete byName[key];
  }
  return { byId, byName };
}

/** Chọn variant theo cỡ hiển thị; URL tuyệt đối đi thẳng. */
export function avatarSrcFor(value: string | null | undefined, sizePx: number): string {
  if (!value) return "";
  // URL tuyệt đối là địa chỉ cuối (ảnh Casdoor, hoặc bản md trên CDN mà máy
  // chủ gửi sẵn cho trang khách — vd người gửi tin chat). Chỉ ngoại lệ: ảnh ERP
  // VỪA tải trong tab này thì bản md chưa có, vẽ bằng ảnh gốc (lib/fresh-upload).
  if (/^https?:\/\//i.test(value)) {
    const key = /\/(profile-photos\/[^/?#]+)\//.exec(value)?.[1];
    return (key && freshOriginalUrl(key)) || value;
  }

  const variant: Variant = sizePx <= 24 ? "thumb" : sizePx <= 56 ? "md" : "lg";

  // Dấu chống cache dành cho CDN, KHÔNG phải một phần khoá lưu trữ: phải tách
  // ra trước khi ghép tên biến thể rồi gắn lại vào cuối. Ghép thẳng sẽ ra
  // ".../S1?v=abc/thumb.webp" → 404.
  const q = value.indexOf("?");

  // Ảnh VỪA tải lên trong tab này: biến thể còn đang sinh, xin nó bây giờ là
  // ăn 404 mà kho ảnh ghim một năm (lib/fresh-upload.ts) — vẽ bằng ảnh gốc.
  const fresh = freshOriginalUrl(q < 0 ? value : value.slice(0, q));
  if (fresh) return fresh;

  if (q < 0) return imageUrl(value, variant);
  const marker = value.slice(q + 1);
  const url = imageUrl(value.slice(0, q), variant);
  if (!url || !marker) return url;
  return url + (url.includes("?") ? "&" : "?") + marker;
}

// Cache theo THAM CHIẾU hai mảng nguồn: ScheduleCell render hàng trăm ô, mỗi
// ô gọi hook này — không cache thì build lại map n_staff lần mỗi ô. TanStack
// Query giữ nguyên reference giữa các render nên WeakMap ăn cache gần 100%.
const mapsCache = new WeakMap<object, WeakMap<object, AvatarMaps>>();
const EMPTY: readonly AvatarSource[] = [];

/**
 * Danh bạ ảnh: mã/tên/ảnh của MỌI nhân sự (GET /staff/avatars). Khoá nằm dưới
 * ["staff"] nên đổi ảnh xong làm mới "staff" là danh bạ cũng tươi theo.
 */
// Ảnh đại diện là thành phần lá, có mặt ở ~90 màn. Cây nào không có
// QueryClientProvider (thành phần dựng riêng lẻ, useStaff đã được thay) thì
// không có gì để gọi mạng: đưa useQuery một client nằm im và tắt truy vấn,
// bản đồ lùi về dòng GET /staff — thay vì ném lỗi làm sập cả màn.
const idleClient = new QueryClient();

function useStaffAvatarDirectory() {
  const provided = useContext(QueryClientContext);
  return useQuery(
    {
      queryKey: ["staff", "avatars"],
      queryFn: async () => {
        const { data, error } = await api.GET("/staff/avatars", {});
        // Không phải mảng (máy chủ lỗi trả thân lạ) ⇒ coi là lỗi: bản đồ lùi về
        // GET /staff thay vì duyệt một object và làm sập cả màn.
        if (error || !Array.isArray(data)) throw new Error("Failed to load staff avatars");
        return data as DirectoryEntry[];
      },
      staleTime: 60_000,
      enabled: provided !== undefined,
    },
    provided ?? idleClient,
  );
}

/**
 * Nguồn chính là danh bạ ảnh: GET /staff cắt về chính mình cho nhân viên
 * thường (phạm vi hồ sơ HR), đọc ảnh từ đó thì trên máy họ mọi đồng nghiệp đều
 * mất ảnh còn trên máy quản trị thì có. Dòng GET /staff vẫn được gộp vào (ghi
 * đè theo mã) để màn nào đã nạp hồ sơ vẫn có ảnh ngay cả khi danh bạ chưa về.
 */
export function useStaffAvatarMap(): AvatarMaps {
  const q = useStaff();
  const dir = useStaffAvatarDirectory();
  return useMemo(() => {
    // Ảnh là thành phần lá ở ~90 màn: nguồn nào trả thân không phải mảng thì
    // coi như rỗng, KHÔNG được làm sập màn chính.
    const dirRows = (Array.isArray(dir.data) ? dir.data : EMPTY) as readonly AvatarSource[];
    const staffRows = (Array.isArray(q.data) ? q.data : EMPTY) as readonly AvatarSource[];
    let inner = mapsCache.get(dirRows);
    const cached = inner?.get(staffRows);
    if (cached) return cached;
    const byIdRows = new Map<string, AvatarSource>();
    for (const r of dirRows) byIdRows.set(r.id, r);
    for (const r of staffRows) {
      // Dòng hồ sơ không mang ảnh thì KHÔNG xoá ảnh danh bạ đã biết.
      const known = byIdRows.get(r.id);
      byIdRows.set(r.id, !r.avatarUrl && known?.avatarUrl ? { ...r, avatarUrl: known.avatarUrl } : r);
    }
    const built = buildAvatarMaps([...byIdRows.values()]);
    if (!inner) {
      inner = new WeakMap();
      mapsCache.set(dirRows, inner);
    }
    inner.set(staffRows, built);
    return built;
  }, [q.data, dir.data]);
}
